/**
 * @fileoverview Renders a map with drawing manager connected so it is possible to draw
 * Contains and operates all logic for draw, save, delete, undo actions
 */
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { connect } from 'react-redux'

import DrawMapView from '../component/DrawMapView'
import DrawingManagerMap from '../../GMap/DrawingManagerMap'
import DrawMapControls from '../components/DrawMapControls/DrawMapControls'
import { drawingManagerOptions } from '../settings'
import ShapeSettingsModal from '../../ShapeSettingsModal/container/ShapeSettingsModal'
import {
  getOverlayBoundary,
  getAreaData,
  generateModalExtraStyles,
  getFilters,
  getDensities,
} from '../util'
import { generateRandomColor } from '../../../Utils/Misc'
import { showReduxModal } from '../../../Reducers/UI/actions'
import {
  saveDraft,
  loadDraft,
  clearDraft,
  listDraftScopes,
} from '../../../Utils/draftStorage'
import { getNumberOfHouseholdsAndDensityForBoundaries } from '../../../Utils/Map'
import {
  getPolygonAreaInKm2,
  getTimeNeededForFlyersDistribution,
} from '../../../shared/libraries/geo'
import { timeConvert } from '../../../shared/libraries/utils/date'

const actionTypes = {
  deleteOverlay: 'deleteOverlay',
  createOverlay: 'createOverlay',
  editOverlay: 'editOverlay',
}

const DrawMap = ({
  onSave,
  initialOverlays,
  onDelete,
  hideMapControls,
  countryCode,
  visibleOverlaysMap,
  onOverlayClick,
  shouldPickWeek = true,
  onMapInit,
  loading,
  mapOptions,
  mapControls = ['DRAW'],
  showReduxModal,
  onDrawingChange = () => {},
  filter,
  onFilterLoaded,
  skipHouseholdsDataOverview,
  onAreaSelect,
}) => {
  const [map, setMap] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [positionInsideParent, setPositionInsideParent] = useState({})
  const [mapHistory, setMapHistory] = useState([])
  const [infoRows, setInfoRows] = useState([])
  const [overlays, setOverlays] = useState([])
  const [selectedOverlay, setSelectedOverlay] = useState(null)
  const [drawingManager, setDrawingManager] = useState(null)
  const [saving, setSaving] = useState(false)
  const initialOverlaysRef = useRef()
  const [densityFilters, setDensityFilters] = useState(null)
  const fullScreenRef = useRef()
  const [isFullScreen, setIsFullScreen] = useState(false)

  const shouldCancelDrawingRef = useRef(false)
  const [isSelecting, setIsSelecting] = useState(false)
  const isSelectingRef = useRef(false)

  /**
   * Effect that clears up after unmount
   */
  useEffect(
    () => () => {
      setOverlays([])
      setSelectedOverlay(null)
      setDrawingManager(null)
    },
    [],
  )

  // Add ESC key handler for stopping drawing
  useEffect(() => {
    const handleKeyDown = event => {
      if (
        event.key === 'Escape' &&
        drawingManager &&
        drawingManager.getDrawingMode()
      ) {
        shouldCancelDrawingRef.current = true
        drawingManager.setDrawingMode(null)
        setIsDrawing(false)
        setIsSelecting(false)
        isSelectingRef.current = false
        onDrawingChange(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [drawingManager, onDrawingChange])

  /**
   * Keep track of the latest initial overlays for
   * onSaveHandler that was created on gmaps overlay render
   * and dont have access to the newest props since he's not rerendered
   */
  useEffect(() => {
    initialOverlaysRef.current = initialOverlays
  }, [initialOverlays])

  /**
   * Effect that hides/shows overlays based on visibleOverlaysMap
   */
  useEffect(() => {
    if (!visibleOverlaysMap) return
    setOverlays(overlays => {
      const densityRange = (densityFilters &&
        filter &&
        densityFilters[filter.toLowerCase()]) || [0, Infinity]

      const toShow = []
      const toHide = []

      overlays.forEach(overlay => {
        let hideByFilter = false
        const hideByVisibleOverlay =
          visibleOverlaysMap && !visibleOverlaysMap[overlay.customId]

        if (filter && filter !== 'ALL' && densityFilters) {
          let [density] =
            'areaData' in overlay
              ? overlay.areaData
                  .find(item => item.name === 'Density')
                  .value.split(' ')
              : [0]
          density = isNaN(+density) ? 0 : +density

          hideByFilter = !(
            density >= densityRange[0] && density < densityRange[1]
          )
        }

        const shouldHide = hideByVisibleOverlay || hideByFilter
        if (shouldHide && overlay.getMap()) {
          toHide.push(overlay)
        } else if (!shouldHide && !overlay.getMap()) {
          toShow.push(overlay)
        }
      })

      // Batch DOM updates in a single frame
      if (toHide.length || toShow.length) {
        requestAnimationFrame(() => {
          toHide.forEach(overlay => overlay.setMap(null))
          toShow.forEach(overlay => overlay.setMap(map))
        })
      }

      return overlays
    })
  }, [visibleOverlaysMap, overlays, densityFilters, filter])

  useEffect(() => {
    if (overlays && overlays.length) {
      setAllAreaData(overlays)
    }
  }, [overlays])

  const saveOverlay = overlay =>
    setOverlays(overlays => {
      const overlaysClone = overlays.filter(existingOverlay => {
        if (overlay.customId == existingOverlay.customId) {
          existingOverlay.setMap(null)
        }
        return overlay.customId != existingOverlay.customId
      })
      return [...overlaysClone, overlay]
    })

  /**
   * Receives overlay and saves it to the overlays array,
   * adds a listener to it,
   * sets it as selected overlay
   * and clears other selections
   *
   * @param {MVCObject} overlay GMAP Overlay
   */
  const onOverlayComplete = async ({ overlay, type }) => {
    if (shouldCancelDrawingRef.current) {
      overlay.setMap(null)
      shouldCancelDrawingRef.current = false
      return
    }
    if (isSelectingRef.current || type === 'rectangle') {
      handleAreaSelectionComplete(overlay)
      return
    }
    stopDrawing(overlay)
    clearSelection()
    const boundary = getOverlayBoundary(overlay)
    if (boundary.length < 3) {
      overlay.setMap(null)
      alert('Overlay should have at least 3 points')
      return
    }
    const randomColor = generateRandomColor()
    overlay.set('fillColor', randomColor)
    overlay.set('strokeColor', randomColor)
    overlay.set('zIndex', 25)
    onSaveHandler({
      overlay,
      callback: processedOverlay => {
        initOverlay(processedOverlay)
        setSelection(processedOverlay)
      },
    })
  }

  /**
   * Receives overlay and sets it as selected overlay after clearing other selections
   * Makes settings modal visible
   *
   * @param {MVCObject} overlay GMAP Overlay
   */
  const setSelection = useCallback(overlay => {
    if (overlay.disabled) return
    clearSelection()
    setSelectedOverlay(() => {
      if (overlay.areaData) {
        setInfoRows(overlay.areaData)
      } else {
        setAreaData(overlay)
      }
      setModalVisible(true)
      overlay.set('strokeWeight', 6)
      if (overlay.editingBlocked) return overlay
      overlay.setEditable(true)
      overlay.setDraggable(true)
      return overlay
    })
  }, [])

  /**
   * Removes selected status from overlay and hides settings modal
   */
  const clearSelection = () =>
    setSelectedOverlay(selectedOverlay => {
      if (selectedOverlay) {
        selectedOverlay.set('strokeWeight', 2)
        selectedOverlay.setEditable(false)
        selectedOverlay.setDraggable(false)
      }
      setModalVisible(false)
      setInfoRows([])
      return null
    })

  /**
   * Wrapper for deleteOverlay that saves action to the map history so it can be reverted
   * @param {MVCObject} overlay
   */
  const deleteOverlayConnected = overlay => {
    deleteOverlay(overlay)

    if (
      overlays.filter(({ customId }) => customId == overlay.customId).length
    ) {
      saveActionToMapHistory({
        type: actionTypes.deleteOverlay,
        payload: {
          overlay,
        },
      })
    }
  }
  /**
   * Deletes overlay and calls related callback provided in props
   * Hides modal and clears selection
   *
   * @param {MVCObject} overlay
   */
  const deleteOverlay = async overlay => {
    if (overlay) {
      setModalVisible(false)
      await onDelete([overlay], false, fullScreenRef.current)
      overlay.setMap(null)
      clearSelection()
      setOverlays(overlays =>
        overlays.filter(({ customId }) => customId !== overlay.customId),
      )
    }
  }

  /**
   * Saves history action to the map history so it can be reverted
   * @param {object} action
   * @param {string} action.type Type of the action - is used to choose reverting action
   * @param {object} action.payload Data that needed for reverting
   */
  const saveActionToMapHistory = action =>
    setMapHistory([...mapHistory, action])

  /**
   * Calls revert on last action that was saved to the map history and pops it, then saves new history
   * Stops drawing
   */
  const undoHandler = async () => {
    stopDrawing()
    setMapHistory(async mapHistory => {
      if (mapHistory && mapHistory.length > 0) {
        const mapHistoryClone = [...mapHistory]
        const lastAction = mapHistoryClone.pop()
        await revertAction(lastAction)
        return mapHistoryClone
      }
    })
  }

  /**
   * Handles reverting of the received action using received type to
   * choose correct reverting action and payload to call with a revert
   *
   * @param {object} action
   * @param {string} action.type Type of the action - is used to choose reverting action
   * @param {object} action.payload Data that needed for reverting
   */
  const revertAction = async action => {
    if (action) {
      const { type, payload } = action
      if (type === actionTypes.createOverlay) {
        await deleteOverlay(payload.overlay)
      }
      if (type === actionTypes.deleteOverlay) {
        const { overlay } = payload

        overlay.customId = null
        overlay.setMap(map)
        await onSaveHandler({ overlay, saveToHistory: false })
      }
      if (type === actionTypes.editOverlay) {
        const { id, paths, color, mapPolygonProps } = payload.prevOverlay

        setOverlays(overlays =>
          overlays.filter(existingOverlay => {
            if (existingOverlay.customId == id) {
              existingOverlay.setMap(null)
            }
            return existingOverlay.customId !== id
          }),
        )

        const overlay = new google.maps.Polygon({
          customId: id,
          key: id,
          clickable: true,
          map: map,
          paths,
          strokeColor: color,
          fillColor: color,
          canBeDeleted: true,
          ...mapPolygonProps,
        })
        initOverlay(overlay)

        await onSaveHandler({
          overlay,
          callback: setSelection,
          saveToHistory: false,
        })
      }
    }
  }

  /**
   * Calls props.onDelete action on all saved overlays
   * Clears selection
   * Hides modal
   */
  const removeAll = async () => {
    if (overlays.length > 0) {
      clearSelection()
      await onDelete(overlays, true, fullScreenRef.current)
      setModalVisible(false)
    }
  }

  /**
   * Toggles drawing mode
   */
  const toggleDrawing = () => {
    if (drawingManager) {
      if (drawingManager.getDrawingMode()) {
        stopDrawing()
      } else {
        startDrawing()
      }
    }
    setModalVisible(false)
  }

  /**
   * Turns off drawing mode
   */
  const stopDrawing = useCallback(
    overlay =>
      setDrawingManager(drawingManager => {
        drawingManager.setDrawingMode(null)
        setIsDrawing(false)
        onDrawingChange(false)
        return drawingManager
      }),
    [],
  )

  /**
   * Turns on drawing mode
   */
  const startDrawing = () =>
    setDrawingManager(drawingManager => {
      drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
      setIsDrawing(true)
      onDrawingChange(true)
      return drawingManager
    })

  const startAreaSelection = () => {
    if (!drawingManager) return
    isSelectingRef.current = true
    setIsSelecting(true)
    setIsDrawing(false)
    onDrawingChange(false)
    drawingManager.setDrawingMode(google.maps.drawing.OverlayType.RECTANGLE)
  }

  const stopAreaSelection = () => {
    if (!drawingManager) return
    isSelectingRef.current = false
    setIsSelecting(false)
    drawingManager.setDrawingMode(null)
  }

  const toggleAreaSelection = () => {
    if (isSelectingRef.current) {
      stopAreaSelection()
    } else {
      startAreaSelection()
    }
    setModalVisible(false)
  }

  const handleAreaSelectionComplete = rectangle => {
    const bounds = rectangle.getBounds()
    rectangle.setMap(null)
    stopAreaSelection()

    const selectedIds = []
    overlays.forEach(overlay => {
      if (!overlay.customId || !overlay.getPath) return
      const path = overlay.getPath()
      for (let i = 0; i < path.getLength(); i++) {
        if (bounds.contains(path.getAt(i))) {
          selectedIds.push(overlay.customId)
          break
        }
      }
    })

    if (selectedIds.length > 0 && onAreaSelect) {
      onAreaSelect(selectedIds)
    }
  }

  // Per-overlay serialized save queue. Replaces a previous global debounce
  // that silently dropped concurrent saves (e.g. typing a label and clicking
  // SAVE while another save was in flight would lose the label).
  const saveQueueRef = useRef(new Map())
  const inFlightCountRef = useRef(0)

  const draftScopeFor = overlay => {
    if (overlay.customId) return `drawmap:id:${overlay.customId}`
    if (overlay.__draftScope) return overlay.__draftScope
    const scope = `drawmap:tmp:${Date.now()}:${Math.random()
      .toString(36)
      .slice(2, 8)}`
    overlay.__draftScope = scope
    return scope
  }

  /**
   * Persist the in-flight save payload to localStorage *before* the network
   * fires so a crashed/dropped/failed save can be recovered on the next
   * mount. Cleared on success.
   */
  const persistDraft = (overlay, payload) => {
    try {
      saveDraft(draftScopeFor(overlay), {
        boundary: getOverlayBoundary(overlay),
        color: overlay.fillColor,
        customId: overlay.customId || null,
        payload,
      })
    } catch (e) {
      // never block the save on storage failure
    }
  }

  const performSave = async ({ overlay, callback, saveToHistory, payload }) => {
    inFlightCountRef.current += 1
    setSaving(true)
    try {
      const isEdit = !!overlay.customId
      if (saveToHistory && isEdit) {
        saveEditActionToHistory(overlay.customId)
      }
      const { customId, fillColor: color } = overlay
      const boundary = getOverlayBoundary(overlay)
      const result = await onSave({
        id: customId,
        color,
        boundary,
        date: payload.date,
        label: payload.label,
        overlay,
        undoHandler,
        modalContainer: fullScreenRef.current,
      })
      overlay.set('customId', result.id)
      overlay.set('key', result.id)
      // Now that we have a real id, migrate the draft scope key so retries
      // line up with the persisted server identity.
      const oldScope = overlay.__draftScope
      const newScope = `drawmap:id:${result.id}`
      if (oldScope && oldScope !== newScope) {
        clearDraft(oldScope)
      }
      overlay.__draftScope = newScope
      callback(overlay)
      // Households/density depend only on the geometry, and getAreaData is a
      // read-only PathFinder lookup. Skip it when the boundary is unchanged
      // (colour / label / week saves) and we already have area data — the
      // cached values and the modal info rows stay correct. Any geometry edit
      // changes the key and recomputes. This never persists user data, so it
      // cannot cause data loss; worst case is a redundant recompute.
      const boundaryKey = JSON.stringify(boundary)
      if (!overlay.areaData || overlay.__areaBoundaryKey !== boundaryKey) {
        setAreaData(overlay)
      }
      if (saveToHistory && !isEdit) {
        saveCreateActionToHistory(overlay)
      }
      clearDraft(newScope)
    } catch (error) {
      console.error(error)
      showReduxModal({
        title: `Overlay saving failed`,
        onConfirm: () => onSaveHandler({ overlay, callback, saveToHistory }),
        onCancel: () => {
          clearSelection()
          if (overlay.customId) {
            undoHandler()
          } else {
            overlay.setMap(null)
          }
        },
        children: (
          <div>
            <p>Reason: {JSON.stringify(error.message)}</p>
            {!!overlay.customId ? (
              <p>Changes will be reverted</p>
            ) : (
              <p>Cancel will remove failed overlay from the map</p>
            )}
            <p style={{ opacity: 0.7, fontSize: 12 }}>
              Your draft is saved locally and will be retried automatically when
              you reload the page.
            </p>
          </div>
        ),
        confirmLabel: 'Retry',
        cancelLabel: 'Cancel',
        container: fullScreenRef.current,
      })
    } finally {
      inFlightCountRef.current -= 1
      if (inFlightCountRef.current <= 0) setSaving(false)
    }
  }

  /**
   * Gets data from selected overlay and calls props.onSave with it.
   * Saves are serialized per overlay so two rapid clicks on the same shape
   * never race or drop each other; saves on different overlays still run
   * concurrently.
   */
  const onSaveHandler = ({
    overlay,
    callback = () => {},
    saveToHistory = true,
    payload = {},
  }) => {
    persistDraft(overlay, payload)
    const queueKey = draftScopeFor(overlay)
    const prev = saveQueueRef.current.get(queueKey) || Promise.resolve()
    const next = prev
      .catch(() => {})
      .then(() => performSave({ overlay, callback, saveToHistory, payload }))
    saveQueueRef.current.set(queueKey, next)
    return next
  }

  /**
   * On mount: scan localStorage for any DrawMap drafts left over from a
   * previous session (crash, refresh mid-save, network failure). They will
   * be visible in the next session if `onSave` is wired — we surface a
   * one-time confirmation so the user can choose to replay them.
   */
  useEffect(() => {
    const pending = listDraftScopes('drawmap:')
    if (!pending.length) return
    const drafts = pending
      .map(scope => ({ scope, data: loadDraft(scope) }))
      .filter(entry => entry.data && entry.data.value)
    if (!drafts.length) return
    const ageOfNewest = Math.max(
      ...drafts.map(d => Date.now() - (d.data.updatedAt || 0)),
    )
    showReduxModal({
      title: 'Recover unsaved map changes?',
      onConfirm: async () => {
        for (const entry of drafts) {
          const { scope, data } = entry
          try {
            await onSave({
              id: data.value.customId,
              color: data.value.color,
              boundary: data.value.boundary,
              date: data.value.payload && data.value.payload.date,
              label: data.value.payload && data.value.payload.label,
              modalContainer: fullScreenRef.current,
            })
            clearDraft(scope)
          } catch (e) {
            // leave it in place — user can retry next time
            console.error('Replay failed for', scope, e)
          }
        }
      },
      onCancel: () => {
        drafts.forEach(d => clearDraft(d.scope))
      },
      children: (
        <div>
          <p>
            We found {drafts.length} unsaved change
            {drafts.length === 1 ? '' : 's'} from a previous session
            {ageOfNewest
              ? ` (newest ~${Math.round(ageOfNewest / 60000)} min ago)`
              : ''}
            .
          </p>
          <p>Restore and re-save them now?</p>
        </div>
      ),
      confirmLabel: 'Restore',
      cancelLabel: 'Discard',
      container: fullScreenRef.current,
    })
    // Intentionally only run once on mount; deps left empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Save create action overlay so it can de deleted on undo
   * @param {object} overlay Overlay
   */
  const saveCreateActionToHistory = overlay =>
    saveActionToMapHistory({
      type: actionTypes.createOverlay,
      payload: {
        overlay,
      },
    })

  /**
   * Save prev state of the edited overlay so it can de undone
   * @param {string} id Overlay's id
   */
  const saveEditActionToHistory = id =>
    saveActionToMapHistory({
      type: actionTypes.editOverlay,
      payload: {
        prevOverlay: initialOverlaysRef.current.find(
          initialOverlay => initialOverlay.id === id,
        ),
      },
    })

  /**
   * Saves cursor position to the components state
   * Cursor position used for displaying settings modal next to the user's cursor
   * @param {object} position
   */
  const handleSetPositionInsideParent = position =>
    setPositionInsideParent(generateModalExtraStyles(position))

  /**
   * Initiates overlay when it is rendered first time from initialOverlays data in the Map component
   * Adds listener and saves to the overlays array
   * @param {MVCObject} overlay
   */
  const initOverlay = overlay => {
    if (
      overlay.customToggled === true &&
      typeof onOverlayClick === 'function'
    ) {
      onOverlayClick(overlay)
    }
    google.maps.event.addListener(overlay, 'click', e => {
      setSelection(overlay)
      if (typeof onOverlayClick === 'function') {
        onOverlayClick(overlay)
      }
    })
    // While the user is dragging the whole polygon, set_at fires once per
    // vertex (a 100-vertex shape would otherwise enqueue 100 sequential
    // saves, freezing the UI and frequently failing mid-chain). Coalesce
    // into a single save when the drag ends.
    overlay.__isDragging = false
    google.maps.event.addListener(overlay, 'dragstart', () => {
      overlay.__isDragging = true
    })
    google.maps.event.addListener(overlay, 'dragend', () => {
      overlay.__isDragging = false
      onSaveHandler({ overlay })
    })
    overlay.getPaths().forEach((path, index) => {
      google.maps.event.addListener(path, 'insert_at', () => {
        if (overlay.__isDragging) return
        onSaveHandler({ overlay }) // New point
      })
      google.maps.event.addListener(path, 'remove_at', () => {
        if (overlay.__isDragging) return
        onSaveHandler({ overlay }) // Point was removed
      })
      google.maps.event.addListener(path, 'set_at', () => {
        if (overlay.__isDragging) return
        onSaveHandler({ overlay }) // Point was moved (single-vertex edits)
      })
    })
    saveOverlay(overlay)
  }
  /**
   * Get area's data
   */
  const setAreaData = async overlay => {
    if (!overlay || !overlay.getPath) return
    // Snapshot the geometry this area data corresponds to, so saves that
    // don't move the boundary (colour / label / week) can skip recomputing.
    const boundaryKey = JSON.stringify(getOverlayBoundary(overlay))
    setInfoRows([
      {
        name: 'Addresses',
        value: 'Loading',
      },
      {
        name: 'Density',
        value: 'Loading',
      },
    ])
    const areaData = await getAreaData(overlay, countryCode)
    setInfoRows(areaData)
    overlay.set('areaData', areaData)
    overlay.__areaBoundaryKey = boundaryKey
  }

  const setAllAreaData = async overlays => {
    if (
      overlays.every(overlay => overlay.areaData) ||
      skipHouseholdsDataOverview
    ) {
      return
    }
    setDensityFilters(null)
    onFilterLoaded && onFilterLoaded(false)
    const boundaries = overlays.map(overlay => getOverlayBoundary(overlay))
    const householdsAndDensities = await getNumberOfHouseholdsAndDensityForBoundaries(
      {
        boundaries,
        countryCode,
      },
    )

    const hashMapHouseholdsAndDensity = overlays.reduce(
      (acc, { customId }, index) => {
        const { density, householdsAmount } = householdsAndDensities[index]
        const polygonAreaSize = getPolygonAreaInKm2(boundaries[index])
        const approximateDistTime = getTimeNeededForFlyersDistribution({
          areaSize: polygonAreaSize,
          householdsAmount: householdsAmount,
        })
        acc[customId] = [
          {
            name: 'Addresses',
            value: householdsAmount,
          },
          {
            name: 'Density',
            value: `${density} HH/km2`,
          },
          {
            name: 'Area Size',
            value: `${polygonAreaSize.toFixed(3)} km2`,
          },
          {
            name: 'Approximate dist time',
            value: timeConvert(approximateDistTime * 60),
          },
        ]
        return acc
      },
      {},
    )

    const onlyMissionsOverlays = overlays.filter(overlay => !!+overlay.customId)

    const newOverlays = onlyMissionsOverlays.map(overlay => {
      const areaData = hashMapHouseholdsAndDensity[overlay.customId]
      overlay.set('areaData', areaData)
      // Tag the geometry so a later colour/label save on a freshly loaded
      // mission can skip recomputing area data (see performSave guard).
      overlay.__areaBoundaryKey = JSON.stringify(getOverlayBoundary(overlay))
      return overlay
    })

    if (newOverlays.length) {
      const densities = getDensities(newOverlays)
      const filters = getFilters(densities)
      setDensityFilters(filters)
      onFilterLoaded && onFilterLoaded(true)
    }
  }

  return (
    <div ref={fullScreenRef}>
      <DrawMapView
        loading={loading || saving}
        setCursorPosition={handleSetPositionInsideParent}
        hideMapControls={hideMapControls}
        isFullScreen={isFullScreen}
        onFullScreen={setIsFullScreen}
        fullScreenElem={fullScreenRef.current}
        controls={
          <DrawMapControls
            isDrawing={isDrawing}
            toggleDrawing={toggleDrawing}
            undoHandler={undoHandler}
            removeAll={removeAll}
            mapControls={mapControls}
            shouldShowUndo={Boolean(mapHistory.length) && !isDrawing}
            saving={saving}
            isFullScreen={isFullScreen}
            onFullScreen={setIsFullScreen}
            isSelecting={isSelecting}
            toggleAreaSelection={toggleAreaSelection}
          />
        }
      >
        <DrawingManagerMap
          setDrawingManager={drawingManager =>
            drawingManager && setDrawingManager(drawingManager)
          }
          onMapInit={map => {
            setMap(map)
            if (onMapInit) {
              onMapInit(map, setModalVisible)
            }
          }}
          onOverlayComplete={onOverlayComplete}
          initOverlay={initOverlay}
          mapOptions={{
            ...mapOptions,
            fullscreenControl: false,
          }}
          drawingManagerOptions={drawingManagerOptions}
          initialOverlays={initialOverlays}
          overlays={overlays}
          isFullScreen={isFullScreen}
        />
        <ShapeSettingsModal
          visible={modalVisible}
          onDelete={() => deleteOverlayConnected(selectedOverlay)}
          onUpdate={(overlay, payload) => onSaveHandler({ overlay, payload })}
          onClose={() => setModalVisible(false)}
          positionInsideParent={positionInsideParent}
          selectedOverlay={selectedOverlay}
          shouldPickWeek={shouldPickWeek}
          countryCode={countryCode}
          infoRows={infoRows}
          saving={saving}
          viewMode={hideMapControls}
        />
      </DrawMapView>
    </div>
  )
}

const mapDispatchToProps = {
  showReduxModal,
}

export default connect(
  null,
  mapDispatchToProps,
)(DrawMap)
